import React, { useState, useEffect, useMemo } from "react";
import useHttp from "../../../hooks/use-http";
import { Spin, Select } from "antd";

import DynamicPieChart from "../../pieChart";
import DynamicColumnChart from "../../columnChart";
import DynamicBarChart from "../../barChart";
import DynamicLineChart from "../../lineChart";

const { Option } = Select;

const OverallSales = ({ isMaximized, openMaximize }) => {
  const [loading, setLoading] = useState(true);

  const [totalSales, setTotalSales] = useState(0);
  const [salesPerProduct, setSalesPerProduct] = useState([]);
  const [salesPerHub, setSalesPerHub] = useState([]);
  const [salesPerCustomer, setSalesPerCustomer] = useState([]);

  const [viewType, setViewType] = useState("product");
  const [chartType, setChartType] = useState("pie");
  const [selectedProduct, setSelectedProduct] = useState("all");
  const [selectedHub, setSelectedHub] = useState("all");
  const [selectedCustomer, setSelectedCustomer] = useState("all");

  const [selectedProductForCustomer, setSelectedProductForCustomer] =
    useState("all");


  const { sendRequest: fetchData } = useHttp();

  const buildUrl = () => {
    const orgId = localStorage.getItem("orgId") || "";
    const params = new URLSearchParams();
    if (orgId) params.append("orgId", orgId);
    return `/api/overallSalesAgriPromoter/?${params.toString()}`;
  };

  useEffect(() => {
    setLoading(true);
    const url = buildUrl();
    fetchData({ url }, (res) => {
      setTotalSales(res?.totalSales || 0);
      setSalesPerProduct(res?.salesPerProduct || []);
      setSalesPerHub(res?.salesPerHub || []);
      setSalesPerCustomer(res?.salesPerCustomer || []);
      setLoading(false);
      setSelectedProduct("all");
      setSelectedProductForCustomer("all");
      setSelectedCustomer("all");
      setSelectedHub("all");
    });
  }, [fetchData]);

  const productOptions = useMemo(
    () => (salesPerProduct || []).map((p) => p.productName).filter(Boolean),
    [salesPerProduct]
  );

  const hubOptions = useMemo(
    () =>
      (salesPerHub || [])
        .map((h) => h.hubName || `Hub ${h.hubId || "Unknown"}`)
        .filter(Boolean),
    [salesPerHub]
  );

  const customerOptions = useMemo(
    () => (salesPerCustomer || []).map((c) => c.customerName).filter(Boolean),
    [salesPerCustomer]
  );

  const productOptionsForSelectedCustomer = useMemo(() => {
    if (selectedCustomer === "all") return [];
    const cust = salesPerCustomer.find(
      (c) => c.customerName === selectedCustomer
    );
    if (!cust || !Array.isArray(cust.products)) return [];
    return Array.from(
      new Set(cust.products.map((p) => p.productName).filter(Boolean))
    );
  }, [salesPerCustomer, selectedCustomer]);

  const customerOptionsForSelectedProduct = useMemo(() => {
    if (selectedProduct === "all") return [];
    const prod = salesPerProduct.find((p) => p.productName === selectedProduct);
    if (!prod || !Array.isArray(prod.customers)) return [];
    return Array.from(
      new Set(prod.customers.map((c) => c.customerName).filter(Boolean))
    );
  }, [salesPerProduct, selectedProduct]);

  let chartDataset = [];
  let displayValue = totalSales;

  if (viewType === "product") {
    if (selectedProduct === "all") {
      chartDataset = (salesPerProduct || []).map((p) => ({
        productName: p.productName,
        totalSales: p.totalSales,
      }));
      displayValue =
        chartDataset.reduce((s, r) => s + (r.totalSales || 0), 0) || totalSales;
    } else {
      const prod = salesPerProduct.find(
        (p) => p.productName === selectedProduct
      ) || { customers: [], totalSales: 0 };
      if (selectedProductForCustomer === "all") {
        const custMap = {};
        (prod.customers || []).forEach((c) => {
          const cust = c.customerName || "Unknown Customer";
          custMap[cust] = (custMap[cust] || 0) + (c.totalSales || 0);
        });
        chartDataset = Object.keys(custMap).map((cust) => ({
          customerName: cust,
          totalSales: custMap[cust],
        }));
        displayValue = chartDataset.reduce(
          (s, r) => s + (r.totalSales || 0),
          0
        );
      } else {
        const hubMap = {};
        (prod.customers || [])
          .filter(
            (c) =>
              (c.customerName || "Unknown Customer") ===
              selectedProductForCustomer
          )
          .forEach((c) => {
            const hub = c.hubName || "Unknown Hub";
            hubMap[hub] = (hubMap[hub] || 0) + (c.totalSales || 0);
          });
        chartDataset = Object.keys(hubMap).map((hub) => ({
          hubName: hub,
          totalSales: hubMap[hub],
        }));
        displayValue = chartDataset.reduce(
          (s, r) => s + (r.totalSales || 0),
          0
        );
      }
    }
  } else if (viewType === "hub") {
    chartDataset =
      selectedHub === "all"
        ? (salesPerHub || []).map((h) => ({
            hubName: h.hubName,
            totalSales: h.totalSales,
          }))
        : (salesPerHub || [])
            .filter((h) => (h.hubName || "").toString() === selectedHub)
            .map((h) => ({ hubName: h.hubName, totalSales: h.totalSales }));
    displayValue =
      chartDataset.reduce((s, r) => s + (r.totalSales || 0), 0) || totalSales;
  } else {
    if (selectedCustomer === "all") {
      chartDataset = (salesPerCustomer || []).map((c) => ({
        customerName: c.customerName,
        totalSales: c.totalSales,
      }));
      displayValue =
        chartDataset.reduce((s, r) => s + (r.totalSales || 0), 0) || totalSales;
    } else {
      const cust = salesPerCustomer.find(
        (c) => c.customerName === selectedCustomer
      ) || { products: [] };
      if (selectedProductForCustomer === "all") {
        chartDataset = (cust.products || []).map((p) => ({
          productName: p.productName,
          totalSales: p.totalSales,
          hubName: p.hubName,
        }));
        displayValue = chartDataset.reduce(
          (s, r) => s + (r.totalSales || 0),
          0
        );
      } else {
        const filtered = (cust.products || []).filter(
          (p) => p.productName === selectedProductForCustomer
        );
        const hubMap = {};
        filtered.forEach((p) => {
          const hub = p.hubName || "Unknown Hub";
          hubMap[hub] = (hubMap[hub] || 0) + (p.totalSales || 0);
        });
        chartDataset = Object.keys(hubMap).map((hub) => ({
          hubName: hub,
          totalSales: hubMap[hub],
        }));
        displayValue = chartDataset.reduce(
          (s, r) => s + (r.totalSales || 0),
          0
        );
      }
    }
  }
 
  const noData =
    !loading &&
    !salesPerProduct.length &&
    !salesPerHub.length &&
    !salesPerCustomer.length;

  if (isMaximized) {
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          padding: 20,
          background: "#f1f7ff",
          borderRadius: 12,
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: 12,
            alignItems: "center",
            marginBottom: 14,
          }}
        >
          <Select
            value={viewType}
            style={{ width: 150 }}
            onChange={(v) => {
              setViewType(v);
              setSelectedProduct("all");
              setSelectedHub("all");
              setSelectedCustomer("all");
              setSelectedProductForCustomer("all");
            }}
          >
            <Option value="product">Product Wise</Option>
            <Option value="hub">Hub Wise</Option>
            <Option value="customer">Customer Wise</Option>
          </Select>

          {viewType === "product" && (
            <>
              <Select
                value={selectedProduct}
                style={{ width: 240 }}
                onChange={(v) => {
                  setSelectedProduct(v);
                  setSelectedProductForCustomer("all");
                }}
              >
                <Option value="all">All Products</Option>
                {productOptions.map((p, i) => (
                  <Option key={i} value={p}>
                    {p}
                  </Option>
                ))}
              </Select>

              {selectedProduct !== "all" && (
                <Select
                  value={selectedProductForCustomer}
                  style={{ width: 260 }}
                  onChange={(v) => setSelectedProductForCustomer(v)}
                >
                  <Option value="all">All Customers (Product)</Option>
                  {customerOptionsForSelectedProduct.map((cname, idx) => (
                    <Option key={idx} value={cname}>
                      {cname}
                    </Option>
                  ))}
                </Select>
              )}
            </>
          )}

          {viewType === "hub" && (
            <Select
              value={selectedHub}
              style={{ width: 240 }}
              onChange={(v) => setSelectedHub(v)}
            >
              <Option value="all">All Hubs</Option>
              {hubOptions.map((h, i) => (
                <Option key={i} value={h}>
                  {h}
                </Option>
              ))}
            </Select>
          )}

          {viewType === "customer" && (
            <>
              <Select
                value={selectedCustomer}
                style={{ width: 260 }}
                onChange={(v) => {
                  setSelectedCustomer(v);
                  setSelectedProductForCustomer("all");
                }}
              >
                <Option value="all">All Customers</Option>
                {customerOptions.map((c, i) => (
                  <Option key={i} value={c}>
                    {c}
                  </Option>
                ))}
              </Select>

              {selectedCustomer !== "all" && (
                <Select
                  value={selectedProductForCustomer}
                  style={{ width: 260 }}
                  onChange={(v) => setSelectedProductForCustomer(v)}
                >
                  <Option value="all">All Products (Customer)</Option>
                  {productOptionsForSelectedCustomer.map((pn, idx) => (
                    <Option key={idx} value={pn}>
                      {pn}
                    </Option>
                  ))}
                </Select>
              )}
            </>
          )}

          <Select
            value={chartType}
            style={{ width: 150, marginLeft: "auto" }}
            onChange={(v) => setChartType(v)}
          >
            <Option value="pie">Pie Chart</Option>
            <Option value="column">Column Chart</Option>
            <Option value="bar">Bar Chart</Option>
            <Option value="line">Line Chart</Option>
          </Select>
        </div>

        {loading ? (
          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Spin />
          </div>
        ) : noData ? (
          <div
            style={{
              flex: 1,
              padding: 24,
              textAlign: "center",
              color: "#666",
            }}
          >
            <h3>No sales data</h3>
          </div>
        ) : (
          <>
            <div
              style={{
                textAlign: "center",
                marginBottom: 12,
                padding: 12,
                background: "#e0ecff",
                borderRadius: 8,
              }}
            >
              <h1 style={{ fontSize: 30, margin: 0, color: "#123e73" }}>
                ₹ {Number(displayValue || 0).toLocaleString()}
              </h1>
              <p style={{ margin: 4, color: "#123e73" }}>
                {viewType === "product"
                  ? selectedProduct === "all"
                    ? "Total Sales (All Products)"
                    : selectedProductForCustomer === "all"
                    ? `Sales — ${selectedProduct} (by customer)`
                    : `Sales — ${selectedProduct} for ${selectedProductForCustomer} (by hub)`
                  : viewType === "hub"
                  ? selectedHub === "all"
                    ? "Total Sales (All Hubs)"
                    : `Sales — ${selectedHub}`
                  : selectedCustomer === "all"
                  ? "Total Sales (All Customers)"
                  : selectedProductForCustomer === "all"
                  ? `Sales for ${selectedCustomer} (by product)`
                  : `Sales of ${selectedProductForCustomer} for ${selectedCustomer} (by hub)`}
              </p>
            </div>

            <div style={{ flex: 1.25, minHeight: 400, overflow: "hidden" }}>
              {viewType === "product" && (
                <>
                  {chartType === "pie" && (
                    <DynamicPieChart
                      data={chartDataset}
                      angleField="totalSales"
                      colorField={
                        selectedProduct === "all"
                          ? "productName"
                          : selectedProductForCustomer === "all"
                          ? "customerName"
                          : "hubName"
                      }
                      isMaximized
                    />
                  )}
                  {chartType === "column" && (
                    <DynamicColumnChart
                      data={chartDataset}
                      xField={
                        selectedProduct === "all"
                          ? "productName"
                          : selectedProductForCustomer === "all"
                          ? "customerName"
                          : "hubName"
                      }
                      yField="totalSales"
                      seriesField={
                        selectedProduct === "all"
                          ? "productName"
                          : selectedProductForCustomer === "all"
                          ? "customerName"
                          : "hubName"
                      }
                      xAxisTitle={
                        selectedProduct === "all"
                          ? "Product"
                          : selectedProductForCustomer === "all"
                          ? "Customer"
                          : "Hub"
                      }
                      yAxisTitle="Sales Amount"
                    />
                  )}
                  {chartType === "bar" && (
                    <DynamicBarChart
                      data={chartDataset}
                      xField={
                        selectedProduct === "all"
                          ? "productName"
                          : selectedProductForCustomer === "all"
                          ? "customerName"
                          : "hubName"
                      }
                      yField="totalSales"
                      seriesField={
                        selectedProduct === "all"
                          ? "productName"
                          : selectedProductForCustomer === "all"
                          ? "customerName"
                          : "hubName"
                      }
                      xAxisTitle={
                        selectedProduct === "all"
                          ? "Product"
                          : selectedProductForCustomer === "all"
                          ? "Customer"
                          : "Hub"
                      }
                      yAxisTitle="Sales Amount"
                    />
                  )}
                  {chartType === "line" && (
                    <DynamicLineChart
                      data={chartDataset}
                      xField={
                        selectedProduct === "all"
                          ? "productName"
                          : selectedProductForCustomer === "all"
                          ? "customerName"
                          : "hubName"
                      }
                      yField="totalSales"
                      seriesField={
                        selectedProduct === "all"
                          ? "productName"
                          : selectedProductForCustomer === "all"
                          ? "customerName"
                          : "hubName"
                      }
                      xAxisTitle={
                        selectedProduct === "all"
                          ? "Product"
                          : selectedProductForCustomer === "all"
                          ? "Customer"
                          : "Hub"
                      }
                      yAxisTitle="Sales Amount"
                    />
                  )}
                </>
              )}

              {viewType === "hub" && (
                <>
                  {chartType === "pie" && (
                    <DynamicPieChart
                      data={chartDataset}
                      angleField="totalSales"
                      colorField="hubName"
                      isMaximized
                    />
                  )}
                  {chartType === "column" && (
                    <DynamicColumnChart
                      data={chartDataset}
                      xField="hubName"
                      yField="totalSales"
                      seriesField="hubName"
                      xAxisTitle="Hub"
                      yAxisTitle="Sales Amount"
                    />
                  )}
                  {chartType === "bar" && (
                    <DynamicBarChart
                      data={chartDataset}
                      xField="hubName"
                      yField="totalSales"
                      seriesField="hubName"
                      xAxisTitle="Hub"
                      yAxisTitle="Sales Amount"
                    />
                  )}
                  {chartType === "line" && (
                    <DynamicLineChart
                      data={chartDataset}
                      xField="hubName"
                      yField="totalSales"
                      seriesField="hubName"
                      xAxisTitle="Hub"
                      yAxisTitle="Sales Amount"
                    />
                  )}
                </>
              )}

              {viewType === "customer" && (
                <>
                  {selectedCustomer === "all" ? (
                    <>
                      {chartType === "pie" && (
                        <DynamicPieChart
                          data={chartDataset}
                          angleField="totalSales"
                          colorField="customerName"
                          isMaximized
                        />
                      )}
                      {chartType === "column" && (
                        <DynamicColumnChart
                          data={chartDataset}
                          xField="customerName"
                          yField="totalSales"
                          seriesField="customerName"
                          xAxisTitle="Customer"
                          yAxisTitle="Sales Amount"
                        />
                      )}
                      {chartType === "bar" && (
                        <DynamicBarChart
                          data={chartDataset}
                          xField="customerName"
                          yField="totalSales"
                          seriesField="customerName"
                          xAxisTitle="Customer"
                          yAxisTitle="Sales Amount"
                        />
                      )}
                      {chartType === "line" && (
                        <DynamicLineChart
                          data={chartDataset}
                          xField="customerName"
                          yField="totalSales"
                          seriesField="customerName"
                          xAxisTitle="Customer"
                          yAxisTitle="Sales Amount"
                        />
                      )}
                    </>
                  ) : (
                    <>
                      {selectedProductForCustomer === "all" ? (
                        <>
                          {chartType === "pie" && (
                            <DynamicPieChart
                              data={chartDataset}
                              angleField="totalSales"
                              colorField="productName"
                              isMaximized
                            />
                          )}
                          {chartType === "column" && (
                            <DynamicColumnChart
                              data={chartDataset}
                              xField="productName"
                              yField="totalSales"
                              seriesField="productName"
                              xAxisTitle="Product"
                              yAxisTitle="Sales Amount"
                            />
                          )}
                          {chartType === "bar" && (
                            <DynamicBarChart
                              data={chartDataset}
                              xField="productName"
                              yField="totalSales"
                              seriesField="productName"
                              xAxisTitle="Product"
                              yAxisTitle="Sales Amount"
                            />
                          )}
                          {chartType === "line" && (
                            <DynamicLineChart
                              data={chartDataset}
                              xField="productName"
                              yField="totalSales"
                              seriesField="productName"
                              xAxisTitle="Product"
                              yAxisTitle="Sales Amount"
                            />
                          )}
                        </>
                      ) : (
                        <>
                          {chartType === "pie" && (
                            <DynamicPieChart
                              data={chartDataset}
                              angleField="totalSales"
                              colorField="hubName"
                              isMaximized
                            />
                          )}
                          {chartType === "column" && (
                            <DynamicColumnChart
                              data={chartDataset}
                              xField="hubName"
                              yField="totalSales"
                              seriesField="hubName"
                              xAxisTitle="Hub"
                              yAxisTitle="Sales Amount"
                            />
                          )}
                          {chartType === "bar" && (
                            <DynamicBarChart
                              data={chartDataset}
                              xField="hubName"
                              yField="totalSales"
                              seriesField="hubName"
                              xAxisTitle="Hub"
                              yAxisTitle="Sales Amount"
                            />
                          )}
                          {chartType === "line" && (
                            <DynamicLineChart
                              data={chartDataset}
                              xField="hubName"
                              yField="totalSales"
                              seriesField="hubName"
                              xAxisTitle="Hub"
                              yAxisTitle="Sales Amount"
                            />
                          )}
                        </>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div
      className="rounded-2xl shadow-md"
      style={{
        background: "#f3e8d3",
        height: 200,
        width: "100%",
        padding: 18,
        position: "relative",
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      {loading ? (
        <div className="flex justify-center items-center h-full">
          <Spin />
        </div>
      ) : noData ? (
        <div
          className="flex justify-center items-center h-full"
          style={{ color: "#3d5c26" }}
        >
          <div style={{ textAlign: "center" }}>
            <h3 style={{ margin: 0 }}>No Sales</h3>
            <p style={{ marginTop: 6, color: "#3d5c26" }}>No sales records</p>
          </div>
        </div>
      ) : (
        <>
          <div style={{ textAlign: "center" }}>
            <h1
              style={{
                // fontSize: 28,
                fontWeight: 700,
                color: "#3d5c26",
                margin: 0,
              }}
            >
              {Number(totalSales || 0).toLocaleString()}
            </h1>
            <h3 style={{ fontWeight: 700, color: "#3d5c26", marginTop: 6 }}>
              Over All Sales
            </h3>
          </div>

          <div
            style={{
              position: "absolute",
              bottom: 10,
              right: 18,
              cursor: "pointer",
              fontWeight: 600,
              color: "#3d5c26",
              fontSize: 14,
            }}
            onClick={openMaximize}
          >
            More...
          </div>
        </>
      )}
    </div>
  );
};

export default OverallSales;
